/**
 * Types and handlers for API endpoints called through the discussions workflow.
 */
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import Log, {Errors, KAError} from "@khan/logging";

import {
    createOfficialClarification,
    deleteOfficialClarification,
    getOfficialClarifications,
    updateOfficialClarification,
} from "#clarifications-package/official-clarifications-queries.ts";
import type {OfficialClarification} from "#clarifications-package/types.ts";
// TODO(somewhatabstract, JIRA-XX13): Improve our patterns so that we
// can pass a valid ApolloGqlClient from the react components.
// DO NOT COPY - bad example of how to get a ApolloGqlClient.
// eslint-disable-next-line no-restricted-imports
import {getUnsafeApolloGqlClient} from "#data-access/gql/get-unsafe-apollo-gql-client.ts";
import {HIDE_PROGRAM_EVERYWHERE} from "#scratchpads-package/queries/hide-program-everywhere.ts";
import {HIDE_PROGRAM_FROM_HOTLIST} from "#scratchpads-package/queries/hide-program-from-hotlist.ts";
import type {QueryParameters} from "#shared-package/format-query-string.ts";

import type {feedbackQuery_feedback as FeedbackQueryFeedback} from "../graphql/__graphql-types__/feedbackQuery.ts";
import type {getFeedbackRepliesPageVariables as FeedbackRepliesPageVars} from "../graphql/__graphql-types__/getFeedbackRepliesPage.ts";
import type {PerformModActionVariables} from "../graphql/__graphql-types__/PerformModAction.ts";
import type {VoteEntityMutationVariables} from "../graphql/__graphql-types__/VoteEntityMutation.ts";
import {DELETE_POST_MUTATION} from "../graphql/delete_post_mutation.ts";
import {FEEDBACK_ANSWERS_QUERY} from "../graphql/feedback_answers.ts";
import {FEEDBACK_QUERY} from "../graphql/feedback_query.ts";
import {FEEDBACK_REPLIES_PAGINATED_QUERY} from "../graphql/feedback_replies.ts";
import {FLAG_POST_MUTATION} from "../graphql/flag_post_mutation.ts";
import {MODIFY_NOTIFICATIONS_FLAG_MUTATION} from "../graphql/modify_notifications_flag.ts";
import {PERFORM_MOD_ACTION} from "../graphql/perform_mod_action.ts";
import {VOTE_ENTITY_MUTATION} from "../graphql/vote_entity_mutation.ts";
import type {
    ContentKind,
    DiscussionsServerResponse,
    FlagType,
    PostKey,
    ServerPost,
} from "../types.ts";
import {getVoteEntityErrorMessage} from "../util.tsx";
import {filterFeedbackList} from "../util/feedback-filters.ts";
import type {GqlSortEnum} from "../util/sort-type-converter.ts";

export type Endpoint<P extends QueryParameters, R> = (params: P) => Promise<R>;

// TODO(LP-10877) rename QueryParameters to GqlVariables
export type GqlEndpoint<P extends QueryParameters, ApolloGqlClient, R> = (
    params: P,
    client: ApolloGqlClient,
) => Promise<R>;

type ModAction =
    | "CHANGE_TYPE"
    | "CLEAR_FLAGS"
    | "LOCK"
    | "PIN"
    | "UNDELETE"
    | "UNLOCK"
    | "UNPIN";

const voteType = {
    UP: 1,
    ABSTAIN: 0,
    DOWN: -1,
} as const;

export type VoteType = (typeof voteType)[keyof typeof voteType];

type AcceptClarification_Parameters = {
    kaid: string;
    openTimestamp: number;
    closeTimestamp: number;
    text: string;
    youtubeId: string;
};

type EditAcceptedClarification_Parameters = AcceptClarification_Parameters & {
    id: PostKey;
    youtubeId: string;
};

export type FetchPosts_Parameters = {
    topicId: string;
    focusKind: ContentKind;
    feedbackType: string;
    currentSort?: GqlSortEnum;
    qaExpandKey?: string | null | undefined;
    limit?: number;
    cursor?: string;
};

const fetchPosts: GqlEndpoint<
    FetchPosts_Parameters,
    ApolloGqlClient,
    DiscussionsServerResponse | null | undefined
> = async (variables, gqlClient) => {
    const response = await gqlClient.query(FEEDBACK_QUERY, {
        variables,
        fetchPolicy: "network-only",
    });
    if (response && response.data && response.data.feedback) {
        return feedbackQueryToFetchPostResponse(
            variables,
            response.data.feedback,
        );
    }
    return null;
};

export const feedbackQueryToFetchPostResponse = (
    variables: FetchPosts_Parameters,
    feedback: FeedbackQueryFeedback,
): DiscussionsServerResponse => {
    const cursor = feedback.cursor ?? "";
    const sortedByDate = feedback.sortedByDate ?? true;
    const isComplete = feedback.isComplete ?? false;
    const filteredFeedback = filterFeedbackList(feedback.feedback ?? []);

    return {
        cursor: cursor,
        feedback: filteredFeedback,
        isComplete: isComplete,
        qaExpandKey: variables.qaExpandKey || "",
        sortedByDate: sortedByDate,
        totalCount: filteredFeedback.length,
        type: variables.feedbackType,
    };
};

const modifyNotificationsFlag: GqlEndpoint<
    {
        postKey: PostKey;
        enable: boolean;
    },
    ApolloGqlClient,
    boolean | null | undefined
> = async (params, gqlClient) => {
    const response = await gqlClient.mutate(
        MODIFY_NOTIFICATIONS_FLAG_MUTATION,
        {
            variables: {key: params.postKey, notifyOnAnswer: params.enable},
        },
    );
    if (response?.data?.modifyNotifyOnAnswer?.feedback) {
        return response.data.modifyNotifyOnAnswer.feedback.notifyOnAnswer;
    }
    return null;
};
const apiVote: GqlEndpoint<
    VoteEntityMutationVariables,
    ApolloGqlClient,
    string | null | undefined
> = (variables, gqlClient) =>
    gqlClient
        .mutate(VOTE_ENTITY_MUTATION, {
            variables,
        })
        .then(({data}) => {
            if (data?.voteEntity?.error) {
                return getVoteEntityErrorMessage(data.voteEntity.error);
            }
            return null;
        })
        .catch((error) => {
            return getVoteEntityErrorMessage({
                code: "INTERNAL",
            });
        });

type VoteActionType = GqlEndpoint<
    {
        postKey: PostKey;
        alreadyVoted?: boolean;
    },
    ApolloGqlClient,
    string | null | undefined
>;

const upvotePost: VoteActionType = ({postKey, alreadyVoted}, gqlClient) =>
    apiVote(
        {
            postKey,
            voteType: alreadyVoted ? voteType.ABSTAIN : voteType.UP,
        },
        gqlClient,
    );

const downvotePost: VoteActionType = ({postKey, alreadyVoted}, gqlClient) =>
    apiVote(
        {
            postKey,
            voteType: alreadyVoted ? voteType.ABSTAIN : voteType.DOWN,
        },
        gqlClient,
    );

// Adapted from discussions/flaggable.py
// Maps flag strings used on the client to server-side strings.
// NOTE(slim): I don't like duplicating this from types.js, but it's the only
// way to avoid both circular dependencies and runtime exports in types.js.
const flagTypes = {
    inappropriate: "inappropriate",
    lowquality: "lowquality",
    shouldBeQuestion: "changetoquestion",
    shouldBeTipsOrThanks: "changetocomment",
    spam: "spam",

    // Only used by CS programs currently
    plagiarism: "plagiarism",
    "vote-soliciting": "vote-soliciting",
    "using-disallowed": "using-disallowed",
} as const;

const flagPost: GqlEndpoint<
    {
        postKey: PostKey;
        flag: FlagType;
    },
    ApolloGqlClient,
    boolean
> = async (params, gqlClient) => {
    const flag = flagTypes[params.flag];
    const response = await gqlClient.mutate(FLAG_POST_MUTATION, {
        variables: {entityKey: params.postKey, flag: flag},
    });
    const success = response?.data?.flagEntity?.error == null ? true : false;
    return success;
};

type PerformModActionEndpointType = (
    vars: Partial<Omit<PerformModActionVariables, "modAction">>,
    client: ApolloGqlClient,
) => Promise<boolean>;

const mutatePerformModAction: (
    arg1: Partial<Omit<PerformModActionVariables, "modAction">>,
    arg2: ApolloGqlClient,
    arg3: ModAction,
) => Promise<boolean> = (args, gqlClient, modAction) => {
    // @ts-expect-error - TS2322 - Type '{ entityKey?: string | undefined; intoType?: "COMMENT" | "PROJECT_EVAL_QUESTION" | "PROJECT_EVAL_ANSWER" | "QUESTION" | "PROJECT_HELP_QUESTION" | "PROJECT_CRITIQUE_QUESTION" | ... 10 more ... | undefined; modAction: ModAction; }' is not assignable to type '{ modAction: "CHANGE_TYPE" | "CLEAR_FLAGS" | "LOCK" | "PIN" | "UNDELETE" | "UNLOCK" | "UNPIN"; entityKey: string; intoType?: "COMMENT" | "PROJECT_EVAL_QUESTION" | "PROJECT_EVAL_ANSWER" | ... 13 more ... | undefined; }'.
    const variables: PerformModActionVariables = {
        modAction,
        ...args,
    };
    return gqlClient
        .mutate(PERFORM_MOD_ACTION, {
            variables,
        })
        .then(({data}) => {
            if (data?.performModAction?.error) {
                return false;
            }
            return true;
        })
        .catch(() => false);
};

const changeType: PerformModActionEndpointType = (vars, client) =>
    mutatePerformModAction(vars, client, "CHANGE_TYPE");

const clearFlags: PerformModActionEndpointType = (vars, client) =>
    mutatePerformModAction(vars, client, "CLEAR_FLAGS");

const undeletePost: PerformModActionEndpointType = (vars, client) =>
    mutatePerformModAction(vars, client, "UNDELETE");

const lockThread: PerformModActionEndpointType = (vars, client) =>
    mutatePerformModAction(vars, client, "LOCK");

const unlockThread: PerformModActionEndpointType = (vars, client) =>
    mutatePerformModAction(vars, client, "UNLOCK");

const pinThread: PerformModActionEndpointType = (vars, client) =>
    mutatePerformModAction(vars, client, "PIN");

const unpinThread: PerformModActionEndpointType = (vars, client) =>
    mutatePerformModAction(vars, client, "UNPIN");

const deletePost: GqlEndpoint<
    {
        postKey: PostKey;
    },
    ApolloGqlClient, // returns true if the delete succeeded
    boolean
> = async (params, gqlClient) => {
    const response = await gqlClient.mutate(DELETE_POST_MUTATION, {
        variables: {postKey: params.postKey},
    });
    const success = response?.data?.hideFeedback?.error?.code == null;
    return success;
};

type HideProgramFn = GqlEndpoint<
    {
        programID: string;
        hide: boolean;
    },
    ApolloGqlClient,
    boolean
>;

const hideProgramEverywhere: HideProgramFn = async (params, gqlClient) => {
    const response = await gqlClient.mutate(HIDE_PROGRAM_EVERYWHERE, {
        variables: {programID: params.programID, hide: params.hide},
    });
    const success = response?.data?.hideProgramEverywhere?.error?.code == null;
    return success;
};

const hideProgramFromHotlist: HideProgramFn = async (params, gqlClient) => {
    const response = await gqlClient.mutate(HIDE_PROGRAM_FROM_HOTLIST, {
        variables: {programID: params.programID, hide: params.hide},
    });
    const success = response?.data?.hideProgramFromHotlist?.error?.code == null;
    return success;
};

const fetchRepliesPaginated: GqlEndpoint<
    FeedbackRepliesPageVars,
    ApolloGqlClient,
    | {
          feedback: Array<ServerPost>;
          cursor: string | null | undefined;
          isComplete: boolean | null | undefined;
      }
    | null
    | undefined
> = (variables, gqlClient) =>
    gqlClient
        .query(FEEDBACK_REPLIES_PAGINATED_QUERY, {
            variables,
            fetchPolicy: "network-only",
        })
        .then(({data}) => {
            if (data?.feedbackRepliesPaginated) {
                const {feedback, cursor, isComplete} =
                    data.feedbackRepliesPaginated;
                return {
                    cursor,
                    isComplete,
                    feedback: filterFeedbackList(feedback || []),
                };
            }
            return null;
        });

const fetchAnswers: GqlEndpoint<
    {
        parentKey: PostKey;
    },
    ApolloGqlClient,
    Array<ServerPost> | null | undefined
> = async ({parentKey}, gqlClient) => {
    if (!parentKey) {
        const error = new KAError(
            "Cannot fetch answers without a questionKey",
            Errors.NotAllowed,
        );
        Log.error(error, Errors.Internal);
        return Promise.reject(error);
    }

    const response = await gqlClient.query(FEEDBACK_ANSWERS_QUERY, {
        variables: {parentKey},
        fetchPolicy: "network-only",
    });
    const parent = response?.data?.feedbackByKey;
    // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'FeedbackAnswers_feedbackByKey'.
    if (parent?.answers) {
        // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'FeedbackAnswers_feedbackByKey'.
        const filteredFeedback = filterFeedbackList(parent.answers);
        return filteredFeedback;
    }
    return null;
};

const fetchAcceptedClarifications: Endpoint<
    {
        youtubeId: string;
    },
    Array<OfficialClarification>
> = (params) => {
    return getOfficialClarifications(
        // TODO(somewhatabstract, JIRA-XX13): Improve our patterns so that we
        // can pass a valid ApolloGqlClient from the react components.
        // eslint-disable-next-line no-restricted-syntax
        getUnsafeApolloGqlClient(),
        params.youtubeId,
    );
};

const acceptClarification: Endpoint<
    AcceptClarification_Parameters,
    OfficialClarification
> = (params) => {
    return createOfficialClarification(
        // TODO(somewhatabstract, JIRA-XX13): Improve our patterns so that we
        // can pass a valid ApolloGqlClient from the react components.
        // eslint-disable-next-line no-restricted-syntax
        getUnsafeApolloGqlClient(),
        params.youtubeId,
        params.kaid,
        params.text,
        params.openTimestamp,
        params.closeTimestamp,
    );
};

const editAcceptedClarification: Endpoint<
    EditAcceptedClarification_Parameters,
    OfficialClarification
> = (params) => {
    return updateOfficialClarification(
        // TODO(somewhatabstract, JIRA-XX13): Improve our patterns so that we
        // can pass a valid ApolloGqlClient from the react components.
        // eslint-disable-next-line no-restricted-syntax
        getUnsafeApolloGqlClient(),
        params.id,
        params.youtubeId,
        params.text,
        params.openTimestamp,
        params.closeTimestamp,
    );
};

const deleteAcceptedClarification: Endpoint<
    {
        clarificationId: string;
    },
    boolean
> = (params) =>
    deleteOfficialClarification(
        // TODO(somewhatabstract, JIRA-XX13): Improve our patterns so that we
        // can pass a valid ApolloGqlClient from the react components.
        // eslint-disable-next-line no-restricted-syntax
        getUnsafeApolloGqlClient(),
        params.clarificationId,
    );

export type DiscussionApi = {
    getId: () => string;
    fetchPosts: GqlEndpoint<
        FetchPosts_Parameters,
        ApolloGqlClient,
        DiscussionsServerResponse | null | undefined
    >;
    modifyNotificationsFlag: GqlEndpoint<
        {
            postKey: PostKey;
            enable: boolean;
        },
        ApolloGqlClient,
        boolean | null | undefined
    >;
    upvotePost: VoteActionType;
    downvotePost: VoteActionType;
    flagPost: GqlEndpoint<
        {
            postKey: PostKey;
            flag: FlagType;
        },
        ApolloGqlClient,
        boolean
    >;
    changeType: PerformModActionEndpointType;
    clearFlags: PerformModActionEndpointType;
    undeletePost: PerformModActionEndpointType;
    lockThread: PerformModActionEndpointType;
    unlockThread: PerformModActionEndpointType;
    pinThread: PerformModActionEndpointType;
    unpinThread: PerformModActionEndpointType;
    deletePost: GqlEndpoint<
        {
            postKey: PostKey;
        },
        ApolloGqlClient,
        boolean
    >;
    hideProgramEverywhere: HideProgramFn;
    hideProgramFromHotlist: HideProgramFn;
    fetchRepliesPaginated: GqlEndpoint<
        FeedbackRepliesPageVars,
        ApolloGqlClient,
        | {
              feedback: Array<ServerPost>;
              cursor: string | null | undefined;
              isComplete: boolean | null | undefined;
          }
        | null
        | undefined
    >;
    fetchAnswers: GqlEndpoint<
        {
            parentKey: PostKey;
        },
        ApolloGqlClient,
        Array<ServerPost> | null | undefined
    >;
    fetchAcceptedClarifications: Endpoint<
        {
            youtubeId: string;
        },
        Array<OfficialClarification>
    >;
    acceptClarification: Endpoint<
        AcceptClarification_Parameters,
        OfficialClarification
    >;
    editAcceptedClarification: Endpoint<
        EditAcceptedClarification_Parameters,
        OfficialClarification
    >;
    deleteAcceptedClarification: Endpoint<
        {
            clarificationId: string;
        },
        boolean
    >;
};

export const defaultApi: DiscussionApi = {
    getId: () => "default",
    fetchPosts,
    modifyNotificationsFlag,
    upvotePost,
    downvotePost,
    flagPost,
    changeType,
    clearFlags,
    deletePost,
    hideProgramEverywhere,
    hideProgramFromHotlist,
    undeletePost,
    lockThread,
    unlockThread,
    pinThread,
    unpinThread,
    fetchAnswers,
    fetchRepliesPaginated,
    fetchAcceptedClarifications,
    acceptClarification,
    editAcceptedClarification,
    deleteAcceptedClarification,
};

const apiMaps: Record<string, DiscussionApi> = {};

const discussionApi = {
    get(id?: string): DiscussionApi {
        const overrides = id && apiMaps[id];

        return overrides || {...defaultApi};
    },
    register(id: string, api: DiscussionApi) {
        apiMaps[id] = api;
    },
} as const;

export default discussionApi;
